// Rescheduling for time slots only

import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import LinearProgress from '@material-ui/core/LinearProgress';
import Checkbox from '@material-ui/core/Checkbox';
import Select from '@material-ui/core/Select';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import Divider from '@material-ui/core/Divider';
import { useNotify } from 'react-admin';
import moment from 'moment';
import { Spin } from 'antd';
import { Typography } from '@material-ui/core';
import WarningOutlineIcon from '@material-ui/icons/ReportProblemOutlined';
import Dialog from '@material-ui/core/Dialog';
import _ from 'lodash';
import dataProvider from '../dataProvider';
import DialogTitle from '../components/DialogTitle';
import { apiGet, apiPost, apiPut } from '../brainApi';
import { getZ3PConfigValue, convertTime, getServiceEntity, trackFullStoryEvent } from '../helper';
import { sendAnalyticsEvent } from '../analytics';
import { useCalendarStyles, renderLegend } from './FullCalendarHelper';
import CalendarAppointment from './CalendarAppointment';

export const timeSlotLineStyle = {
  timeSlotLaneWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    position: 'relative',
  },
  timeSlotCell: {
    position: 'relative',
    width: '100%',
    top: -12,
  },
  timeSlotLine: {
    width: '90%',
    margin: '0 auto',
    height: 2,
    position: 'absolute',
    top: 10,
    left: 5,
    zIndex: -1,
    background: 'rgba(101,101,101, .6)',
  },
  startTimeSlotStyle: {
    height: 0,
    opacity: '1 !important',
    color: 'black',
    background: 'transparent',
    backgroundColor: 'transparent',
    border: 'none',
  },
  timeSlotWrapper: {
    position: 'relative',
    zIndex: 1,
  },
};

const useStyles = makeStyles((theme) => ({
  modalWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'scroll',
    '& .MuiDialog-container.MuiDialog-scrollPaper': {
      flexBasis: '850px',
    },
  },
  vehicleSelect: {
    marginLeft: theme.spacing(1),
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  warningIcon: {
    verticalAlign: 'middle',
    fontSize: '20px',
    color: '#D64747',
    margin: '5px 10px 5px -3px',
  },
  ...timeSlotLineStyle,
}));

export const COLOR_LEGEND = {
  today: {
    color: '#72bcf6',
    text: 'Today',
  },
  availability: {
    color: 'rgba(144, 238, 144, 0.3)',
    text: 'Available Slots',
  },
  nonBusinessHours: {
    color: 'rgb(128, 128, 128, .2)',
    text: `${getServiceEntity(false, 'capitalization')} not available`,
  },
};

export const getAllUniqueCompatibleDates = (res, dates, allCompatibleDates) => {
  const compatibleDates = [];
  dates.forEach((date) => {
    res[date].forEach((availability) => {
      compatibleDates.push({ ...availability, date });
    });
  });
  const fullCompatibleDatesList = [...allCompatibleDates, ...compatibleDates];
  return _.uniqBy(fullCompatibleDatesList, (item) =>
    [item.date, item.service_vehicle_id].join(),
  );
};

export const updatePropertiesHelper = (
  propsToUpdate,
  events,
  swId,
  timeIsUnavailable,
  timeZone,
) => {
  const apptIdx = events.findIndex((e) => e.id === swId);
  const updatedEventObject = { ...events[apptIdx], ...propsToUpdate };
  if (
    !timeIsUnavailable &&
    updatedEventObject.startStr === moment().tz(timeZone)
  ) {
    updatedEventObject.borderColor = 'green';
  }
  const eventsCopy = [...events];
  eventsCopy.splice(apptIdx, 1, updatedEventObject);
  return eventsCopy;
};

export const setViewVariables = (times, viewRange, timeZone, setViewRange) => {
  let earliestTime = viewRange[0];
  let latestTime = viewRange[1];
  times.forEach((time) => {
    let start = moment(time.start)
      .subtract(1, 'hours')
      .startOf('hour');
    let end = moment(time.end)
      .add(1, 'hours')
      .startOf('hour');
    if (!start.isSame(moment(time.start), 'day'))
      start = moment(time.start).startOf('day');
    if (!end.isSame(moment(time.end), 'day'))
      end = moment(time.end).endOf('day');
    if (timeZone) {
      start = moment(start)
        .tz(timeZone)
        .format('HH:mm:00');
      end = moment(end)
        .tz(timeZone)
        .format('HH:mm:00');
    } else {
      start = moment(start).format('HH:mm:00');
      end = moment(end).format('HH:mm:00');
    }
    if (start < earliestTime) earliestTime = start;
    if (end > latestTime) latestTime = end;
  });
  setViewRange([earliestTime, latestTime]);
};

export const renderTimeZone = (timeZone) => (
  <Box marginTop={1}>
    {timeZone && (
      <span
        style={{
          fontStyle: 'italic',
          fontSize: '0.75rem',
        }}
      >
        All times are shown in <strong>{timeZone}</strong>.
      </span>
    )}
  </Box>
);

// Find and format existing appointment from response for /get-dispatch-info to pass into <CalendarAppointment>
// If rescheduling an appointment, show dynamic event times instead of the appointment's scheduled times
export const getAndFormatAppointment = (
  scheduleData,
  eventInfo,
  useEventTimes = false,
) => {
  const foundAppointment = scheduleData
    .flatMap((x) => x.appointments)
    .find((appt) => appt.scheduled_work_id === Number(eventInfo.id));
  if (foundAppointment) {
    return {
      start_time: useEventTimes
        ? eventInfo.start
        : foundAppointment.confirmed_appointment_start_time_utc,
      end_time: useEventTimes
        ? eventInfo.end
        : foundAppointment.expected_appointment_end_time_utc,
      buffer_time_minutes: foundAppointment.buffer_time_minutes,
      drive_time_minutes: foundAppointment.drive_time_minutes,
      total_labor_hours: foundAppointment.total_labor_hours,
      location_type: foundAppointment.location_type,
    };
  }
  return null;
};

const isOnStartTimeSlot = (
  startTime,
  isTimeAvailable,
  allCompatibleDates,
  selectedServiceVehicleId,
  timeZone,
) => {
  const selectedDate = moment(startTime)
    .tz(timeZone)
    .format('YYYY-MM-DD');
  const selectedTime = moment(startTime)
    .tz(timeZone)
    .format('HH:mm:00');
  const newDatesForServiceVehicle = allCompatibleDates.filter(
    (d) => d.service_vehicle_id === selectedServiceVehicleId,
  );
  newDatesForServiceVehicle.sort((a, b) => new Date(a.date) - new Date(b.date));
  const availableTimeSlot = newDatesForServiceVehicle.filter(
    (item) => moment(item.date).format('YYYY-MM-DD') === selectedDate,
  )?.[0]?.predetermined_time_slots;

  return !!(
    availableTimeSlot &&
    availableTimeSlot.find((item) => item === selectedTime) &&
    isTimeAvailable
  );
};

export const borderColor = (
  startTime,
  isTimeAvailable,
  allCompatibleDates,
  selectedServiceVehicleId,
  theme,
  is_using_time_blocks,
  timeZone,
) => {
  if (
    is_using_time_blocks &&
    isOnStartTimeSlot(
      startTime,
      isTimeAvailable,
      allCompatibleDates,
      selectedServiceVehicleId,
      timeZone,
    )
  ) {
    return theme.palette.info.dark;
  }
  if (isTimeAvailable) {
    return theme.palette.info.dark;
  }

  return '#cd6163';
};

export const RenderSlotLaneTime = (classes) => {
  return (
    <div className={classes.timeSlotLaneWrapper}>
      <div className={classes.timeSlotCell}>
        <div className={classes.timeSlotWrapper}>
          <div className={classes.timeSlotLine} />
        </div>
      </div>
    </div>
  );
};

export const calcTimeSlotTimes = (
  newDatesForServiceVehicle,
  selectedServiceVehicleId,
  classes,
) => {
  const startTimeSlotEvents = [];
  newDatesForServiceVehicle.forEach((dateObj) => {
    if (
      dateObj.predetermined_time_slots?.length > 0 &&
      dateObj.predetermined_time_slots
    ) {
      dateObj.predetermined_time_slots.forEach((item) => {
        const date = moment(dateObj.date)
          .set({ hour: item.split(':')[0], minute: item.split(':')[1] })
          .format('ddd, DD MMM YYYY HH:mm:00');
        startTimeSlotEvents.push({
          groupId: 'availableForAppointment',
          start: new Date(`${date} EST`),
          display: 'background',
          extendedProps: {
            department: 'startTime',
            time: new Date(`${date} EST`),
          },
          classNames: [classes.startTimeSlotStyle],
        });
      });
    }
  });
  return startTimeSlotEvents;
};

export const isTimeWithinAvailabilityRange = (
  datetime,
  calculateEndTime,
  availableTimeEvents,
) => {
  const end = moment(calculateEndTime(datetime));
  return availableTimeEvents.some(
    (timeObj) =>
      moment(timeObj.start) <= datetime &&
      moment(timeObj.end) >= end &&
      timeObj.backgroundColor === 'lightgreen',
  );
};

const RescheduleModal = ({ open, setOpen, sw }) => {
  const classes = useStyles();
  const calendarClasses = useCalendarStyles();
  const notify = useNotify();
  const theme = useTheme();
  const lastDispatchInfoPromise = useRef();
  const confirmationEmailTemplateId = getZ3PConfigValue(
    'confirmation_postmark_template_id',
  );
  const confirmationSmsTemplateId = getZ3PConfigValue(
    'appointment_confirmation_sms_template_id',
  );
  const timeRangeMin = convertTime(
    getZ3PConfigValue('time_range_min'),
    'militaryString',
  );
  const { is_using_time_blocks } = getZ3PConfigValue();

  const [viewRange, setViewRange] = useState(['08:00:00', '19:00:00']);
  const [isResendEmailChecked, setIsResendEmailChecked] = useState(true);
  const [serviceVehicleList, setServiceVehicleList] = useState([]);
  const [selectedServiceVehicleId, setSelectedServiceVehicleId] = useState('');
  const [scheduleData, setScheduleData] = useState([]);
  const [timeZone, setTimeZone] = useState('America/New_York');
  const [events, setEvents] = useState([]);
  const [isLoadingCompatibleDates, setIsLoadingCompatibleDates] = useState(
    false,
  );
  const [isLoadingScheduleData, setIsLoadingScheduleData] = useState(false);
  const [
    isLoadingAdditionalScheduleData,
    setIsLoadingAdditionalScheduleData,
  ] = useState(false);
  const [allCompatibleDates, setAllCompatibleDates] = useState([]);
  const [
    datesForSelectedServiceVehicle,
    setDatesForSelectedServiceVehicle,
  ] = useState([]);
  const [availableTimeEvents, setAvailableTimeEvents] = useState([]);
  const [startTimeSlots, setStartTimeSlots] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasBeenModified, setHasBeenModified] = useState(false);
  const [timeIsUnavailable, setTimeIsUnavailable] = useState(false);
  const [conflictReason, setConflictReason] = useState(null);
  const [selectedDateState, setSelectedDateState] = useState(null);
  const [canSaveEvent, setCanSaveEvent] = useState(false);
  const [startDateLoaded, setStartDateLoaded] = useState(null);
  const [endDateLoaded, setEndDateLoaded] = useState(null);

  const fetchServiceVehicles = () => {
    dataProvider
      .getList('service-vehicle', {
        filter: {
          is_active: true,
          is_service_bay: sw.location_type === 'shop',
        },
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 2000 },
      })
      .then(({ data }) => {
        setServiceVehicleList(data);
        setSelectedServiceVehicleId(sw.service_vehicle_id);
      })
      .catch((error) => {
        console.log('Error: ', error);
      });
  };

  const getCompatibleDates = (startDate = null, endDate = null) => {
    const services = sw.services
      .filter((s) => !s.is_upsell || s.upsell_status === 'approved')
      .filter((s) => s.status !== 'DELETED');
    const serviceOfferingIds = services
      .map((service) => service.service_offering_id)
      .join(',');
    const totalLaborHours = services.reduce((total, service) => {
      return total + service.labor_hours;
    }, 0);
    const params = {
      use_new_version: true,
      requested_service_offering_ids: serviceOfferingIds,
      total_labor_hours: totalLaborHours,
    };
    if (sw.client_location_id) {
      params.client_location_id = sw.client_location_id;
    } else {
      params.home_address_id = sw.customer_address_id;
    }
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }

    apiGet('/get-compatible-dates', params)
      .then((res) => {
        if (res) {
          const dates = Object.keys(res).sort();
          if (dates.length > 0) {
            const allUniqueCompatibleDates = getAllUniqueCompatibleDates(
              res,
              dates,
              allCompatibleDates,
            );
            setAllCompatibleDates(allUniqueCompatibleDates);
          }
        }
      })
      .catch((error) => {
        console.log('Error: ', error);
        notify('Failed to find available dates', 'warning');
      })
      .finally(() => {
        setIsLoadingCompatibleDates(false);
      });
  };

  useEffect(() => {
    if (open) {
      // Only show loading indicator on initial load
      setIsLoadingCompatibleDates(true);
      setIsLoadingScheduleData(true);
      fetchServiceVehicles();
    }
  }, [sw, open]);

  const updateAppointmentProperties = (propsToUpdate) => {
    const updatedEvents = updatePropertiesHelper(
      propsToUpdate,
      events,
      sw.id,
      timeIsUnavailable,
      timeZone,
    );
    setEvents(updatedEvents);
  };

  // Make appointment editable only once compatible dates are loaded, otherwise event gets duplicated
  useEffect(() => {
    updateAppointmentProperties({
      startEditable:
        !isLoadingAdditionalScheduleData && !isLoadingCompatibleDates,
    });
  }, [isLoadingAdditionalScheduleData, isLoadingCompatibleDates]);

  const calculateAppointmentDurationMinutes = () => {
    const services = sw.services
      .filter((s) => !s.is_upsell || s.upsell_status === 'approved')
      .filter((s) => s.status !== 'DELETED');
    const laborHours = services.reduce((total, service) => {
      return total + service.labor_hours;
    }, 0);
    return laborHours * 60;
  };

  const calculateEndTime = (start) => {
    if (start) {
      return moment(start)
        .add(calculateAppointmentDurationMinutes(), 'minutes')
        .format();
    }
    return null;
  };

  // If services have been edited, update appointment end time
  useEffect(() => {
    const start = `${sw.date}T${sw.confirmed_appointment_start_time}`;
    const end = calculateEndTime(start);
    updateAppointmentProperties({ end });
  }, [sw.services]);

  const selectTime = (momentDatetime, revert = () => {}) => {
    const selectedDate = momentDatetime.format('YYYY-MM-DD');
    const matchingSchedule = datesForSelectedServiceVehicle.find(
      (si) => si?.date === selectedDate,
    );
    setSelectedDateState(selectedDate);
    if (matchingSchedule) {
      setCanSaveEvent(true);
      setHasBeenModified(true);
      const startTime = momentDatetime.format();
      const endTime = calculateEndTime(startTime);
      const isTimeAvailable =
        matchingSchedule?.is_available &&
        isTimeWithinAvailabilityRange(
          momentDatetime,
          calculateEndTime,
          availableTimeEvents,
        );
      if (isTimeAvailable) {
        setConflictReason(null);
      } else {
        const unavailableReason =
          matchingSchedule?.unavailable_reason ||
          `${momentDatetime.tz(timeZone).format('h:mm a')} - Time Not Free`;
        setConflictReason(unavailableReason);
      }
      setTimeIsUnavailable(!isTimeAvailable);
      const renderedBorderColor = borderColor(
        startTime,
        isTimeAvailable,
        allCompatibleDates,
        selectedServiceVehicleId,
        theme,
        is_using_time_blocks,
        timeZone,
      );
      updateAppointmentProperties({
        start: startTime,
        end: endTime,
        backgroundColor: 'white',
        startEditable:
          !isLoadingAdditionalScheduleData && !isLoadingCompatibleDates,
        textColor: isTimeAvailable ? theme.palette.info.dark : '#cd6163',
        borderColor: renderedBorderColor,
      });
    } else {
      revert();
    }
  };

  const getBeginningOfLastWeek = (shownDate = undefined) => {
    return moment(shownDate)
      .subtract(1, 'week')
      .startOf('week')
      .format('YYYY-MM-DD');
  };
  const getEndOfNextWeek = (shownDate = undefined) => {
    return moment(shownDate)
      .add(1, 'week')
      .endOf('week')
      .format('YYYY-MM-DD');
  };

  const fetchScheduleData = (startDate, endDate) => {
    setIsLoadingAdditionalScheduleData(true);
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    return apiGet('/get-dispatch-info', params)
      .then((response) => {
        setIsLoadingScheduleData(false);
        setIsLoadingAdditionalScheduleData(false);
        if (!response || response.status === 'error') {
          notify('Failed to fetch schedule data', 'warning');
          return [];
        }
        setStartDateLoaded(startDate);
        setEndDateLoaded(endDate);
        return response;
      })
      .catch((error) => {
        setIsLoadingScheduleData(false);
        setIsLoadingAdditionalScheduleData(false);
        console.log('Error: ', error);
        notify('Failed to fetch schedule data', 'warning');
      });
  };

  const getEvents = ({ startStr }) => {
    const dateCopy = moment
      .parseZone(startStr.split('T')[0])
      .format('YYYY-MM-DD');
    const startDate = moment
      .parseZone(dateCopy)
      .startOf('week')
      .format('YYYY-MM-DD');
    const endDate = moment
      .parseZone(dateCopy)
      .endOf('week')
      .format('YYYY-MM-DD');
    if (startDate === startDateLoaded || endDate === endDateLoaded) {
      return;
    }
    const currentDispatchInfoPromise = fetchScheduleData(startDate, endDate);
    lastDispatchInfoPromise.current = currentDispatchInfoPromise;
    currentDispatchInfoPromise.then((response) => {
      // Set time zone regardless of which call it's from since we do not support operations in multiple timezones yet
      if (response.time_zone && response.time_zone !== timeZone) {
        setTimeZone(response.time_zone);
      }
      if (currentDispatchInfoPromise === lastDispatchInfoPromise.current) {
        const fullSchedule = [...scheduleData, ...response.schedule];
        const allUniqueSchedules = _.uniqBy(fullSchedule, (item) =>
          [item.schedule_instance_id, item.service_vehicle_id].join(),
        );
        setScheduleData(allUniqueSchedules);
      }
    });
    getCompatibleDates(startDate, endDate);
  };

  // When compatible dates loads or selected sv changes, filter all compatible dates to the ones that are available and for the selected service vehicle
  useEffect(() => {
    const newAvailableTimes = [];
    const newDatesForServiceVehicle = allCompatibleDates.filter(
      (d) => d.service_vehicle_id === selectedServiceVehicleId,
    );
    newDatesForServiceVehicle.sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );
    const startTimeSlotEvents = calcTimeSlotTimes(
      newDatesForServiceVehicle,
      selectedServiceVehicleId,
      classes,
    );
    newDatesForServiceVehicle.forEach((dateObj) => {
      if (dateObj.is_available && dateObj.available_time_ranges) {
        dateObj.available_time_ranges.forEach((timeRange) => {
          newAvailableTimes.push({
            groupId: 'availableForAppointment',
            start: moment(timeRange[0])
              .tz(timeZone)
              .format(),
            end: moment(timeRange[1])
              .tz(timeZone)
              .format(),
            display: 'background',
            backgroundColor: 'lightgreen',
          });
        });
      }
      // In addition to adding actual availability, also add background availability
      // for all dates with a schedule to allow for scheduling overrides
      const start = moment(dateObj.date)
        .set({ hour: 0, minute: 0 })
        .tz(timeZone, true)
        .format();
      const end = moment(dateObj.date)
        .set({ hour: 23, minute: 59 })
        .tz(timeZone, true)
        .format();
      newAvailableTimes.push({
        groupId: 'availableForAppointment',
        start,
        end,
        display: 'inverse-background',
        backgroundColor: 'rgb(128, 128, 128)',
      });
    });
    newAvailableTimes.sort((a, b) => a.start - b.start);
    setDatesForSelectedServiceVehicle(newDatesForServiceVehicle);
    setAvailableTimeEvents(newAvailableTimes);
    setStartTimeSlots(startTimeSlotEvents);
    setViewVariables(newAvailableTimes, viewRange, timeZone, setViewRange);
  }, [allCompatibleDates, selectedServiceVehicleId]);

  const renderAppointmentEvents = () => {
    if (!scheduleData.length) {
      return [];
    }
    const schedules = scheduleData.filter(
      (schedule) => selectedServiceVehicleId === schedule.service_vehicle_id,
    );

    const appointmentToReschedule = events.find((e) => Number(e.id) === sw.id);
    const allEvents = appointmentToReschedule ? [appointmentToReschedule] : [];
    const allEventIds = allEvents.map((event) => Number(event.id));

    schedules.forEach((schedule) => {
      const activeAppointments = schedule.appointments.filter(
        (appt) => appt.status !== 'cancelled' && appt.status !== 'suggested',
      );
      // appointmentsToRender should include all appointments except the appointment to reschedule if it's already in events
      const appointmentsToRender = activeAppointments.filter(
        (appt) =>
          appt.scheduled_work_id !== sw.id ||
          (appt.scheduled_work_id === sw.id && !allEventIds.includes(sw.id)),
      );

      const appointments = appointmentsToRender.map((appointment) => {
        const {
          scheduled_work_id,
          drive_start_time_utc,
          buffer_end_time_utc,
          confirmed_appointment_start_time_utc,
          expected_appointment_end_time_utc,
          customer,
        } = appointment;
        let eventObject = {
          id: scheduled_work_id,
          backgroundColor: '#6D9ECA',
          borderColor: '#6D9ECA',
          start: drive_start_time_utc,
          end: buffer_end_time_utc,
          title: `${customer.first_name} ${customer.last_name}`,
          display: 'auto',
        };
        if (scheduled_work_id === sw.id) {
          const rescheduleProps = {
            backgroundColor: 'white',
            borderColor: theme.palette.info.dark,
            textColor: theme.palette.info.dark,
            constraint: 'availableForAppointment',
            start: confirmed_appointment_start_time_utc,
            end: expected_appointment_end_time_utc,
            startEditable:
              !isLoadingAdditionalScheduleData && !isLoadingCompatibleDates,
          };
          eventObject = { ...eventObject, ...rescheduleProps };
        }
        return eventObject;
      });

      allEvents.push(appointments);
    });
    setEvents(allEvents.flat());
  };

  // When user changes service vehicle or date range, filter all appointment events to match the current view.
  useEffect(() => {
    renderAppointmentEvents();
  }, [selectedServiceVehicleId, scheduleData, timeIsUnavailable]);

  const handleClose = () => {
    setOpen(false);
    // Reset appointment
    const start = `${sw.date}T${sw.confirmed_appointment_start_time}`;
    // Format to remove time zone from end to match the start
    const end = moment(calculateEndTime(start)).format('YYYY-MM-DDTHH:mm:ss');
    updateAppointmentProperties({
      start,
      end,
      startEditable: false,
      textColor: theme.palette.info.dark,
      borderColor: theme.palette.info.dark,
    });
    setHasBeenModified(false);
    setIsResendEmailChecked(true);
    setTimeIsUnavailable(false);
    setConflictReason(null);
  };

  const handleSubmit = () => {
    setIsSaving(true);
    const appointment = events.find((e) => Number(e.id) === sw.id);
    const date = moment(appointment.start)
      .tz(timeZone)
      .format('YYYY-MM-DD');
    const time = moment(appointment.start)
      .tz(timeZone)
      .format('HH:mm:00');
    const payload = {
      service_vehicle_id: selectedServiceVehicleId,
      date,
      confirmed_appointment_start_time: time,
      send_confirmation_to_customer:
        confirmationEmailTemplateId && isResendEmailChecked,
    };
    trackFullStoryEvent('CTA_Appointment_Details_Reschedule_Submit', {
      appointment_id: sw?.id,
      new_date: date,
      new_time: time,
      service_vehicle_id: selectedServiceVehicleId,
    });
    apiPut(`/scheduled-work/${sw.id}`, payload)
      .then(() => {
        setIsSaving(false);
        handleClose();
        notify('Appointment rescheduled successfully');
        window.location.reload();
      })
      .catch((error) => {
        console.log('Error: ', error);
        setIsSaving(false);
        handleClose();
        notify('Failed to reschedule appointment', 'warning');
      });
  };

  useEffect(() => {
    const selectedDate = selectedDateState || sw.date;
    const dateIsAvailable = datesForSelectedServiceVehicle.some(
      (si) => si?.date === selectedDate,
    );
    const appointmentEventExists = events.some((e) => e.id === sw.id);
    if (!dateIsAvailable && appointmentEventExists) {
      setEvents((prevState) => prevState.filter((e) => e.id === sw.id));
      setCanSaveEvent(false);
    } else {
      setCanSaveEvent(true);
    }
  }, [sw, selectedDateState, datesForSelectedServiceVehicle]);

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      className={classes.modalWrapper}
      open={open}
      onClose={handleClose}
    >
      <Paper>
        <DialogTitle onClose={handleClose}>Reschedule Appointment</DialogTitle>
        {isLoadingAdditionalScheduleData && !isLoadingScheduleData ? (
          <LinearProgress style={{ height: '2px' }} />
        ) : (
          <Divider />
        )}
        <DialogContent>
          <Box display="flex" justifyContent="center" m={2}>
            {serviceVehicleList.length > 1 && (
              <>
                <LocalShippingIcon fontSize="medium" color="primary" />
                <Select
                  className={classes.vehicleSelect}
                  value={selectedServiceVehicleId}
                  onChange={(e) => setSelectedServiceVehicleId(e.target.value)}
                >
                  {serviceVehicleList.map((sv) => (
                    <MenuItem key={sv.id} value={sv.id} name={sv.name}>
                      {sv.name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </Box>
          <Box className={calendarClasses.weekViewCalendar}>
            <Spin
              indicator={<CircularProgress />}
              spinning={isLoadingCompatibleDates || isLoadingScheduleData}
            >
              <FullCalendar
                aspectRatio="1.5"
                plugins={[
                  timeGridPlugin,
                  interactionPlugin,
                  momentTimezonePlugin,
                ]}
                initialView="timeGridWeek"
                schedulerLicenseKey={
                  process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY
                }
                timeZone={timeZone || 'local'}
                initialDate={sw.date}
                allDayContent={false}
                events={[...events, ...availableTimeEvents, ...startTimeSlots]}
                datesSet={getEvents}
                contentHeight="500px"
                slotDuration="00:30:00"
                snapDuration="00:15:00"
                defaultTimedEventDuration="00:30"
                eventMinHeight={30}
                expandRows
                slotMinTime="00:00:00"
                slotMaxTime="23:59:00"
                scrollTime={timeRangeMin}
                slotLabelInterval="01:00"
                weekends
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                }}
                displayEventTime
                headerToolbar={{
                  left: 'prev',
                  center: 'title',
                  right: 'today next',
                }}
                eventDrop={({ event: { startStr }, revert }) =>
                  selectTime(moment(startStr), revert)
                }
                dateClick={(data) => {
                  selectTime(moment(data.dateStr));
                }}
                eventContent={(e) => {
                  if (
                    e.event.extendedProps?.department === 'startTime' &&
                    is_using_time_blocks
                  ) {
                    return RenderSlotLaneTime(classes);
                  }
                  if (e.event.display.includes('background')) return null;
                  return (
                    <CalendarAppointment
                      eventInfo={e}
                      appointment={
                        Number(e.event.id) === sw.id
                          ? getAndFormatAppointment(scheduleData, e.event, true)
                          : getAndFormatAppointment(scheduleData, e.event)
                      }
                      timeZone={timeZone}
                      timeIsUnavailable={timeIsUnavailable}
                      conflictReason={e.isDraggable ? conflictReason : null}
                    />
                  );
                }}
              />
            </Spin>
          </Box>
          {renderTimeZone(timeZone)}
          {timeIsUnavailable && conflictReason && (
            <Box>
              <Typography>
                <WarningOutlineIcon className={classes.warningIcon} />
                <strong style={{ color: '#D64747' }}>{conflictReason}</strong>
              </Typography>
            </Box>
          )}
          {renderLegend(COLOR_LEGEND)}

          <Box className={classes.center} mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isResendEmailChecked}
                  name="resend-confirmation-email"
                  color="primary"
                />
              }
              label={`Resend confirmation ${[
                confirmationEmailTemplateId && sw.customer?.email && 'email',
                confirmationSmsTemplateId && 'SMS',
              ]
                .filter(Boolean)
                .join(' and ')} to customer`}
              onChange={(e) => setIsResendEmailChecked(e.target.checked)}
            />
          </Box>
        </DialogContent>
        <Box className={classes.center} mb={2}>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!hasBeenModified || isSaving || !canSaveEvent}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSaving ? 'Saving...' : 'Reschedule Appointment'}
            </Button>
          </DialogActions>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default RescheduleModal;
